// =============================================================================
// components/call/CallInProgressBanner.tsx — WhatsApp-style "call in progress"
// bar (P2-CALLS-BANNER).
//
// A persistent, tappable strip shown across the app while a 1:1 call OR a voice
// room is active — the CRYB lavender-glass analogue of WhatsApp's green
// "tap to return to call" bar. It is driven by a tiny module-level active-call
// STORE (below) so any screen can flip it on/off without prop-drilling or a new
// Context provider:
//
//   • VoiceCallScreen / VoiceRoomScreen call setActiveCall(...) once connected
//     and clearActiveCall() in their guarded teardown.
//   • The banner subscribes via useActiveCall() and renders null when idle
//     (reserves no space), so it is safe to mount anywhere.
//   • Tapping it navigates back to the live surface via navigationRef (works
//     from above the navigator too).
//
// It is mounted in the primary landing surface (app/(tabs)/home.tsx) today. The
// store is exported so App.tsx can later host it globally above the navigator
// for true app-wide coverage (see followUps) — no API change needed for that.
//
// DESIGN: tokens only (GlassSurface + colorTokens/typographyScale/spacing/
// radius/animationTokens). No hardcoded hex/rgba/fontSize/radius/shadow.
// =============================================================================
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Animated, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GlassSurface from '../glass/GlassSurface';
import { Ionicons } from '../ui/Icon';
import {
  colorTokens,
  spacing,
  radius,
  typographyScale,
  animationTokens,
} from '../../constants/theme';
import { navigate } from '../../navigation/navigationRef';
import type { RootStackParamList } from '../../navigation/types';

// ── Active-call store ────────────────────────────────────────────────────────
// A minimal external store (useSyncExternalStore) so the live-call state can be
// set from a screen and read from a banner mounted elsewhere, with no Context.
export type ActiveCallKind = 'call' | 'room';

export interface ActiveCall {
  /** Which live surface to return to. */
  kind: ActiveCallKind;
  /** Route + params to navigate back to the live screen. */
  route: keyof RootStackParamList;
  params?: RootStackParamList[keyof RootStackParamList];
  /** Primary line (peer name / room title). */
  title: string;
  /** Optional secondary line (e.g. "Voice room" / "Audio call"). */
  subtitle?: string;
  /** Epoch ms the call connected — drives the live duration timer. */
  startedAt: number;
}

let currentCall: ActiveCall | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a bad listener must never break the store */
    }
  });
}

/** Flip the banner ON. Call once the live media path is up. Idempotent-safe. */
export function setActiveCall(call: Omit<ActiveCall, 'startedAt'> & { startedAt?: number }): void {
  currentCall = { ...call, startedAt: call.startedAt ?? Date.now() };
  emit();
}

/** Flip the banner OFF. Safe to call multiple times (no-ops when already idle). */
export function clearActiveCall(): void {
  if (currentCall === null) return;
  currentCall = null;
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): ActiveCall | null {
  return currentCall;
}

/** Read the active call reactively. Returns null when no call/room is live. */
export function useActiveCall(): ActiveCall | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// mm:ss (or h:mm:ss past an hour) for the live duration counter.
function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// ── Banner ───────────────────────────────────────────────────────────────────
export default function CallInProgressBanner() {
  const call = useActiveCall();
  const insets = useSafeAreaInsets();

  // Live 1s duration tick — only running while a call is active.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!call) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [call?.startedAt]);

  // Slide/fade the strip in when a call starts.
  const anim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(anim, {
      toValue: call ? 1 : 0,
      duration: animationTokens.duration.base,
      useNativeDriver: true,
    }).start();
  }, [call, anim]);

  if (!call) return null;

  const duration = formatDuration(now - call.startedAt);
  const label = call.subtitle ?? (call.kind === 'room' ? 'Voice room' : 'On a call');

  return (
    <Animated.View
      style={[
        styles.wrap,
        {
          top: insets.top + spacing.xs,
          opacity: anim,
          transform: [
            {
              translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [-12, 0] }),
            },
          ],
        },
      ]}
      pointerEvents="box-none"
    >
      <GlassSurface
        mode="light"
        radius={radius.full}
        surfaceColor={colorTokens.dark.success}
        borderColor={colorTokens.dark.border}
        onPress={() => navigate(call.route, call.params as any)}
        accessibilityLabel={`${label} in progress with ${call.title}, ${duration}. Tap to return to call.`}
        style={styles.surface}
        contentStyle={styles.content}
      >
        <View style={styles.iconWrap}>
          <Ionicons
            name={call.kind === 'room' ? 'people' : 'call'}
            size={16}
            color={colorTokens.dark.text}
          />
        </View>
        <View style={styles.meta}>
          <Text style={styles.title} numberOfLines={1}>
            {call.title}
          </Text>
          <Text style={styles.sub} numberOfLines={1}>
            {label} · {duration}
          </Text>
        </View>
        <View style={styles.returnChip}>
          <Text style={styles.returnText}>Tap to return</Text>
          <Ionicons name="chevron-forward" size={14} color={colorTokens.dark.text} />
        </View>
      </GlassSurface>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: 'absolute',
    left: spacing.md,
    right: spacing.md,
    zIndex: 100,
  },
  surface: {
    width: '100%',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorTokens.dark.border,
  },
  meta: {
    flex: 1,
  },
  title: {
    ...typographyScale.body,
    fontWeight: '700',
    color: colorTokens.dark.text,
  },
  sub: {
    ...typographyScale.caption,
    color: colorTokens.dark.textMuted,
    marginTop: 1,
  },
  returnChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  returnText: {
    ...typographyScale.caption,
    fontWeight: '700',
    color: colorTokens.dark.text,
  },
});
